// src/routes/index.tsx

import React, { Suspense } from 'react';
import { useRoutes, Navigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { MainLayout } from '@/components/layout/MainLayout';
import { LoadingSpinner } from '@guardian/shared-ui';
import { readStorageValue, writeStorageValue, removeStorageValue } from '@guardian/shared-utils';
import { FormValidationGuard } from '@/components/guards/FormValidationGuard';
import { ErrorFallback } from '@/components/ErrorFallback';
import { FormDataBrandRedirect } from '@/components/branding/FormDataBrandRedirect';
import { PreviewBrandingProvider } from '@/contexts/PreviewBrandingProvider';
import { PreviewDataProvider } from '@/contexts/PreviewDataProvider';
import { PREVIEW_FORM_ID } from '@/lib/previewFixtures';

// Retry wrapper for dynamic imports.
// On final failure (stale chunks after deploy), reloads the page once
// so the browser fetches the new index.html with updated chunk references.
const RELOAD_KEY = 'chunk_reload';

// Storage access itself throws SecurityError ("The operation is insecure.") in private
// browsing, with cookies blocked, or inside an in-app webview -- the common case here,
// because guests arrive from WhatsApp, Instagram and OTA links that open in the host
// app's own browser. `@guardian/shared-utils/safeStorage` absorbs that; see its header.

/**
 * Claim the single allowed reload.
 *
 * Returns true only when no reload has been recorded AND the claim was actually
 * persisted. That second condition is load-bearing: without storage we cannot remember
 * that we already reloaded, and a reload we cannot remember is an infinite reload loop.
 * Storage blocked therefore means NO reload -- the caller rethrows and the guest gets
 * one error screen instead of a cycling phone.
 */
function claimReload(): boolean {
  if (readStorageValue('session', RELOAD_KEY)) return false;
  return writeStorageValue('session', RELOAD_KEY, '1');
}

/** Release the claim so a LATER deploy is allowed its own single reload. */
function releaseReloadClaim(): void {
  removeStorageValue('session', RELOAD_KEY);
}

// Exported for tests (lazy-with-retry-storage.test.tsx), following the same
// module-scope-export convention as `routes` below: the reload-once semantics and the
// blocked-storage path are not reachable through the route map, because the route map
// only ever hands this real page chunks that resolve.
export function lazyWithRetry<T extends React.ComponentType>(
  importFn: () => Promise<{ default: T }>,
  retries = 2,
  delay = 1000
): React.LazyExoticComponent<T> {
  return React.lazy(() => {
    const tryImport = async (attempt: number): Promise<{ default: T }> => {
      try {
        const mod = await importFn();
        // # WHY here and not in the factory body: the release must happen on SUCCESS.
        // Released before the import was attempted, the flag could never survive to be
        // read, so `claimReload` always saw an empty slot and every stale-chunk load
        // reloaded again -- the "reload once" guard never fired once.
        releaseReloadClaim();
        return mod;
      } catch (error) {
        if (attempt < retries) {
          await new Promise(resolve => setTimeout(resolve, delay));
          return tryImport(attempt + 1);
        }
        // After retries exhausted, reload once to get fresh chunks.
        if (claimReload()) {
          window.location.reload();
        }
        throw error;
      }
    };
    // The factory body touches no storage: it runs during React's render phase, so a
    // throw here unwinds past every route to the ErrorBoundary and blanks the app.
    return tryImport(0);
  });
}

// Lazy load components with retry for Safari compatibility
const HomePage = lazyWithRetry(() => import('@/pages/home-page'));
const FormPage = lazyWithRetry(() => import('@/pages/form/FormPage'));
const GuestFormPage = lazyWithRetry(() => import('@/pages/form/GuestFormPage'));
const VehicleFormPage = lazyWithRetry(() => import('@/pages/form/VehicleFormPage'));
const PetFormPage = lazyWithRetry(() => import('@/pages/form/PetFormPage'));
const GuestCheckinPage = lazyWithRetry(() => import('@/pages/guest-checkin/GuestCheckinPage'));
const ConnectionErrorPage = lazyWithRetry(() => import('@/pages/ConnectionErrorPage'));

const SuspenseWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense
    fallback={
      <div className="flex h-[calc(100dvh-4rem)] w-full items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    }
  >
    {children}
  </Suspense>
);

const PreserveQueryNavigate: React.FC<{ to: string }> = ({ to }) => {
  const search = typeof window !== 'undefined' ? window.location.search : '';
  return <Navigate to={`${to}${search}`} replace />;
};

// Module-scope export so tests (forms-plural-alias.test.tsx) can drive useRoutes(routes).
// Short-form /f/* and /l/* entries are the new canonical (Decision 31g / PRD form-app-white-labeling.md,
// appended in Story 32.9); plural /forms/* and singular /form/* entries are kept as back-compat aliases
// for the FR-DOM-2 transition window so URLs from Story 32.1 emission (now superseded) continue to resolve.
// /checkin/:listingPublicId is also a back-compat alias; /l/:listingPublicId is the new primary.
export const routes = [
  {
    path: '/',
    element: (
      <MainLayout>
        <SuspenseWrapper>
          <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
            <HomePage />
          </Sentry.ErrorBoundary>
        </SuspenseWrapper>
      </MainLayout>
    ),
  },
  {
    path: '/f/:formId',
    element: (
      <MainLayout>
        <SuspenseWrapper>
          <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
            <FormDataBrandRedirect>
              <FormValidationGuard>
                <FormPage />
              </FormValidationGuard>
            </FormDataBrandRedirect>
          </Sentry.ErrorBoundary>
        </SuspenseWrapper>
      </MainLayout>
    ),
  },
  {
    path: '/f/:formId/guest/:guestId',
    element: (
      <MainLayout>
        <SuspenseWrapper>
          <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
            <FormDataBrandRedirect>
              <FormValidationGuard>
                <GuestFormPage />
              </FormValidationGuard>
            </FormDataBrandRedirect>
          </Sentry.ErrorBoundary>
        </SuspenseWrapper>
      </MainLayout>
    ),
  },
  {
    path: '/f/:formId/vehicle/:vehicleId',
    element: (
      <MainLayout>
        <SuspenseWrapper>
          <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
            <FormDataBrandRedirect>
              <FormValidationGuard>
                <VehicleFormPage />
              </FormValidationGuard>
            </FormDataBrandRedirect>
          </Sentry.ErrorBoundary>
        </SuspenseWrapper>
      </MainLayout>
    ),
  },
  {
    path: '/f/:formId/pet/:petId',
    element: (
      <MainLayout>
        <SuspenseWrapper>
          <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
            <FormDataBrandRedirect>
              <FormValidationGuard>
                <PetFormPage />
              </FormValidationGuard>
            </FormDataBrandRedirect>
          </Sentry.ErrorBoundary>
        </SuspenseWrapper>
      </MainLayout>
    ),
  },
  {
    path: '/l/:listingPublicId',
    element: (
      <MainLayout>
        <SuspenseWrapper>
          <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
            <GuestCheckinPage />
          </Sentry.ErrorBoundary>
        </SuspenseWrapper>
      </MainLayout>
    ),
  },
  {
    path: '/checkin/:listingPublicId',
    element: (
      <MainLayout>
        <SuspenseWrapper>
          <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
            <GuestCheckinPage />
          </Sentry.ErrorBoundary>
        </SuspenseWrapper>
      </MainLayout>
    ),
  },
  {
    path: '/forms/:formId',
    element: (
      <MainLayout>
        <SuspenseWrapper>
          <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
            <FormDataBrandRedirect>
              <FormValidationGuard>
                <FormPage />
              </FormValidationGuard>
            </FormDataBrandRedirect>
          </Sentry.ErrorBoundary>
        </SuspenseWrapper>
      </MainLayout>
    ),
  },
  {
    path: '/forms/:formId/guest/:guestId',
    element: (
      <MainLayout>
        <SuspenseWrapper>
          <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
            <FormDataBrandRedirect>
              <FormValidationGuard>
                <GuestFormPage />
              </FormValidationGuard>
            </FormDataBrandRedirect>
          </Sentry.ErrorBoundary>
        </SuspenseWrapper>
      </MainLayout>
    ),
  },
  {
    path: '/forms/:formId/vehicle/:vehicleId',
    element: (
      <MainLayout>
        <SuspenseWrapper>
          <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
            <FormDataBrandRedirect>
              <FormValidationGuard>
                <VehicleFormPage />
              </FormValidationGuard>
            </FormDataBrandRedirect>
          </Sentry.ErrorBoundary>
        </SuspenseWrapper>
      </MainLayout>
    ),
  },
  {
    path: '/forms/:formId/pet/:petId',
    element: (
      <MainLayout>
        <SuspenseWrapper>
          <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
            <FormDataBrandRedirect>
              <FormValidationGuard>
                <PetFormPage />
              </FormValidationGuard>
            </FormDataBrandRedirect>
          </Sentry.ErrorBoundary>
        </SuspenseWrapper>
      </MainLayout>
    ),
  },
  {
    path: '/form/:formId',
    element: (
      <MainLayout>
        <SuspenseWrapper>
          <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
            <FormDataBrandRedirect>
              <FormValidationGuard>
                <FormPage />
              </FormValidationGuard>
            </FormDataBrandRedirect>
          </Sentry.ErrorBoundary>
        </SuspenseWrapper>
      </MainLayout>
    ),
  },
  {
    path: '/form/:formId/guest/:guestId',
    element: (
      <MainLayout>
        <SuspenseWrapper>
          <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
            <FormDataBrandRedirect>
              <FormValidationGuard>
                <GuestFormPage />
              </FormValidationGuard>
            </FormDataBrandRedirect>
          </Sentry.ErrorBoundary>
        </SuspenseWrapper>
      </MainLayout>
    ),
  },
  {
    path: '/preview/listing',
    element: (
      <PreviewBrandingProvider>
        <PreviewDataProvider kind="listing">
          <MainLayout>
            <SuspenseWrapper>
              <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
                <GuestCheckinPage />
              </Sentry.ErrorBoundary>
            </SuspenseWrapper>
          </MainLayout>
        </PreviewDataProvider>
      </PreviewBrandingProvider>
    ),
  },
  {
    // # WHY a redirect hop: FormPage reads formId via useParams and bails to "/"
    // when it's undefined. The dashboard iframe loads `/preview/form` (no id),
    // so we hand off to the same route with a stable preview id appended. The
    // PreviewBrandingProvider lives ONLY on the destination so the postMessage
    // PREVIEW_READY signal fires once. Preserve the brand query so
    // "Open in new tab" survives the redirect.
    path: '/preview/form',
    element: <PreserveQueryNavigate to={`/preview/form/${PREVIEW_FORM_ID}`} />,
  },
  {
    path: '/preview/form/:formId',
    element: (
      <PreviewBrandingProvider>
        <PreviewDataProvider kind="form">
          <MainLayout>
            <SuspenseWrapper>
              <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
                <FormPage />
              </Sentry.ErrorBoundary>
            </SuspenseWrapper>
          </MainLayout>
        </PreviewDataProvider>
      </PreviewBrandingProvider>
    ),
  },
  {
    // # WHY: mirrors the real wizard path `/form/:formId/guest/:guestId` so the dashboard's
    // Check-in preview can click Register and land on the branded multi-step wizard.
    // Submissions inside this surface no-op via the apiClient preview-mode short-circuit
    // and the explicit GuestFormPage preview-mode toast.
    path: '/preview/form/:formId/guest/:guestId',
    element: (
      <PreviewBrandingProvider>
        <PreviewDataProvider kind="form">
          <MainLayout>
            <SuspenseWrapper>
              <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
                <GuestFormPage />
              </Sentry.ErrorBoundary>
            </SuspenseWrapper>
          </MainLayout>
        </PreviewDataProvider>
      </PreviewBrandingProvider>
    ),
  },
  {
    path: '/connection-error',
    element: (
      <SuspenseWrapper>
        <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
          <ConnectionErrorPage />
        </Sentry.ErrorBoundary>
      </SuspenseWrapper>
    ),
  },
  {
    path: "/form/:formId/vehicle/:vehicleId",
    element: (
      <MainLayout>
        <SuspenseWrapper>
          <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
            <FormDataBrandRedirect>
              <FormValidationGuard>
                <VehicleFormPage />
              </FormValidationGuard>
            </FormDataBrandRedirect>
          </Sentry.ErrorBoundary>
        </SuspenseWrapper>
      </MainLayout>
    ),
  },
  {
    path: "/form/:formId/pet/:petId",
    element: (
      <MainLayout>
        <SuspenseWrapper>
          <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
            <FormDataBrandRedirect>
              <FormValidationGuard>
                <PetFormPage />
              </FormValidationGuard>
            </FormDataBrandRedirect>
          </Sentry.ErrorBoundary>
        </SuspenseWrapper>
      </MainLayout>
    ),
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
];

export const AppRoutes = () => useRoutes(routes);
